import { useEffect, useMemo, useRef, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

type SiteReplayStat = {
  siteId: string;
  siteName: string;
  replayCount: number;
  totalDurationMs: number;
  lastReplayEndTime: number;
};

type EmployeeDoubleHitSummary = {
  employeeId: number;
  employeeName: string;
  flagCount: number;
  flaggedTourCount: number;
  lastFlagTime: number;
};

type EmployeeDoubleHitDetail = {
  flagKey: string;
  employeeId: number;
  employeeName: string;
  siteId: string;
  siteName: string;
  previousScanTime: number;
  currentScanTime: number;
  minutesBetween: number;
  previousTourSessionId: number | null;
  currentTourSessionId: number;
  currentTourId: number | null;
  currentTourName: string;
  previousCheckpointId: number;
  previousCheckpointName: string;
  currentCheckpointId: number;
  currentCheckpointName: string;
};

type EmployeeDoubleHitProcessingStatus = {
  initialized: boolean;
  backfillCompleted: boolean;
  totalScansProcessed: number;
  totalFlagsCreated: number;
  lastRunAt: number;
  lastError: string | null;
};

type QuickPatrolFlag = {
  visitId: string;
  siteId: string;
  siteName: string;
  vehicleId: string;
  vehicleName: string;
  guardEmployeeId: number | null;
  guardName: string;
  patrolDurationMs: number;
  averageDurationMs: number;
  quickThresholdMs: number;
  percentFaster: number;
  replayEndTime: number;
  sampleSize: number;
};

type TrackerType = "patrol_replay" | "employee_double_hits" | "quick_patrols";
type EmployeeSortBy =
  | "flags_desc"
  | "flags_asc"
  | "tours_desc"
  | "tours_asc"
  | "recent_desc"
  | "name_asc"
  | "name_desc";

type AnalyticsHeaderState = {
  title: string;
};

type AnalyticsProps = {
  onHeaderStateChange?: (state: AnalyticsHeaderState) => void;
  onQuickPatrolReplay?: (visit: any) => void;
  sidebarBackRequestNonce?: number;
};

const PST_TIMEZONE = "Etc/GMT+8";

/* ── Utility helpers ── */

function formatDuration(ms: number | null | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms <= 0) return "-";
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatDateTimeLocal(ts: number | null | undefined): string {
  if (typeof ts !== "number" || !Number.isFinite(ts)) return "-";
  return new Date(ts).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatDateTimePSTCompact(ts: number | null | undefined): string {
  if (typeof ts !== "number" || !Number.isFinite(ts)) return "-";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: PST_TIMEZONE,
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(ts));
}

function formatDatePST(ts: number | null | undefined): string {
  if (typeof ts !== "number" || !Number.isFinite(ts)) return "-";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: PST_TIMEZONE,
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(new Date(ts));
}

function formatClockPST(ts: number | null | undefined): string {
  if (typeof ts !== "number" || !Number.isFinite(ts)) return "-";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: PST_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(ts));
}

function normalizeMinutesBetween(minutesBetween: number, previousScanTime: number, currentScanTime: number): number {
  if (typeof minutesBetween === "number" && Number.isFinite(minutesBetween) && minutesBetween > 0) {
    return Math.round(minutesBetween);
  }
  const diffMs = currentScanTime - previousScanTime;
  if (!Number.isFinite(diffMs) || diffMs <= 0) return 0;
  return Math.max(1, Math.round(diffMs / (60 * 1000)));
}

function getEmployeeLabel(employeeName: string, employeeId: number): string {
  return employeeName || `Guard ${employeeId}`;
}

function getInitials(name: string): string {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ── Inline SVG Icons ── */

function IconReplay() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3.333 10A6.667 6.667 0 0 1 10 3.333c2.127 0 4.001 1 5.213 2.547L16.667 4.167V8.333H12.5l1.653-1.653A4.986 4.986 0 0 0 10 5a5 5 0 1 0 5 5h1.667A6.667 6.667 0 0 1 3.333 10Z"
        fill="currentColor"
      />
    </svg>
  );
}

function IconFlag() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4.167 2.5v15M4.167 12.5s.833-.833 3.333-.833 4.167 1.666 6.667 1.666c1.25 0 2.083-.333 2.5-.5V3.333c-.417.167-1.25.5-2.5.5-2.5 0-4.167-1.666-6.667-1.666-2.5 0-3.333.833-3.333.833V12.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconBolt() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11.25 1.667 4.583 10h4.584L8.75 18.333 15.417 10h-4.584L11.25 1.667Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5 10.5l3.5-3.5L5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconClock() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 4.5V8l2.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconActivity() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14.667 8H12l-2 5.333L6 2.667 4 8H1.333"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconAlertTriangle() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.99 2.28 1.058 10.5A1.167 1.167 0 0 0 2.066 12.25h9.868a1.167 1.167 0 0 0 1.008-1.75L8.01 2.28a1.167 1.167 0 0 0-2.02 0ZM7 5.25v2.333M7 9.917h.006"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSort() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3.5 8.75l3.5 3.5 3.5-3.5M10.5 5.25L7 1.75 3.5 5.25"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════
   ANALYTICS COMPONENT
   ══════════════════════════════════════════════════════════ */

export default function Analytics({
  onHeaderStateChange,
  onQuickPatrolReplay,
  sidebarBackRequestNonce = 0,
}: AnalyticsProps) {
  const patrolStats = useQuery(api.analytics.getPatrolReplaySiteAverages, {
    days: 180,
    maxVisits: 300,
  }) as SiteReplayStat[] | undefined;
  const employeeFlags = useQuery((api as any).employeeDoubleHits.getEmployeeDoubleHitSummary, {
    limit: 400,
  }) as EmployeeDoubleHitSummary[] | undefined;
  const quickPatrolFlags = useQuery((api as any).analytics.getQuickPatrolFlags, {
    days: 60,
    maxFlags: 400,
    maxVisits: 800,
  }) as QuickPatrolFlag[] | undefined;
  const processingStatus = useQuery(
    (api as any).employeeDoubleHits.getEmployeeDoubleHitProcessingStatus,
    {}
  ) as EmployeeDoubleHitProcessingStatus | undefined;

  const [tracker, setTracker] = useState<TrackerType | null>(null);
  const [search, setSearch] = useState("");
  const [employeeSortBy, setEmployeeSortBy] = useState<EmployeeSortBy>("flags_desc");
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeDoubleHitSummary | null>(null);
  const [loadingQuickReplayVisitId, setLoadingQuickReplayVisitId] = useState<string | null>(null);
  const lastHandledSidebarBackRef = useRef<number>(0);

  const kickEmployeeBackfill = useAction((api as any).employeeDoubleHits.kickEmployeeDoubleHitProcessingNow);
  const getQuickPatrolReplayVisit = useMutation((api as any).analytics.getQuickPatrolReplayVisit);

  const employeeDetails = useQuery(
    (api as any).employeeDoubleHits.getEmployeeDoubleHitDetails,
    selectedEmployee
      ? {
          employeeId: selectedEmployee.employeeId,
          limit: 500,
        }
      : ("skip" as any)
  ) as EmployeeDoubleHitDetail[] | undefined;

  const isTrackerSelected = tracker !== null;
  const isPatrolReplayTracker = tracker === "patrol_replay";
  const isEmployeeTracker = tracker === "employee_double_hits";
  const isQuickPatrolTracker = tracker === "quick_patrols";

  useEffect(() => {
    if (!isEmployeeTracker) return;
    void kickEmployeeBackfill({
      maxBatches: 1,
      limitPerBatch: 300,
    }).catch(() => undefined);
  }, [isEmployeeTracker, kickEmployeeBackfill]);

  useEffect(() => {
    if (!isEmployeeTracker && selectedEmployee) {
      setSelectedEmployee(null);
    }
  }, [isEmployeeTracker, selectedEmployee]);

  /* ── Computed data ── */

  const patrolRows = useMemo(() => {
    const list = (patrolStats || []).map((item) => ({
      ...item,
      avgMs: item.replayCount > 0 ? item.totalDurationMs / item.replayCount : 0,
    }));
    const q = search.trim().toLowerCase();
    const filtered = q
      ? list.filter((item) => {
          const name = (item.siteName || "").toLowerCase();
          const id = (item.siteId || "").toLowerCase();
          return name.includes(q) || id.includes(q);
        })
      : list;
    return [...filtered].sort((a, b) => {
      if (b.replayCount !== a.replayCount) return b.replayCount - a.replayCount;
      if (b.lastReplayEndTime !== a.lastReplayEndTime) return b.lastReplayEndTime - a.lastReplayEndTime;
      return (a.siteName || a.siteId).localeCompare(b.siteName || b.siteId);
    });
  }, [patrolStats, search]);

  const employeeRows = useMemo(() => {
    const list = employeeFlags || [];
    const q = search.trim().toLowerCase();
    const filtered = q
      ? list.filter((item) => {
          const name = (item.employeeName || "").toLowerCase();
          const id = String(item.employeeId || "").toLowerCase();
          return name.includes(q) || id.includes(q);
        })
      : list;
    return [...filtered].sort((a, b) => {
      switch (employeeSortBy) {
        case "flags_asc": return a.flagCount - b.flagCount;
        case "tours_desc": return b.flaggedTourCount - a.flaggedTourCount;
        case "tours_asc": return a.flaggedTourCount - b.flaggedTourCount;
        case "recent_desc": return b.lastFlagTime - a.lastFlagTime;
        case "name_asc": return (a.employeeName || "").localeCompare(b.employeeName || "");
        case "name_desc": return (b.employeeName || "").localeCompare(a.employeeName || "");
        case "flags_desc":
        default: return b.flagCount - a.flagCount;
      }
    });
  }, [employeeFlags, search, employeeSortBy]);

  const quickPatrolRows = useMemo(() => {
    const list = quickPatrolFlags || [];
    const q = search.trim().toLowerCase();
    const filtered = q
      ? list.filter((item) => {
          const site = (item.siteName || "").toLowerCase();
          const guard = (item.guardName || "").toLowerCase();
          const vehicle = (item.vehicleName || "").toLowerCase();
          const vehicleId = (item.vehicleId || "").toLowerCase();
          return site.includes(q) || guard.includes(q) || vehicle.includes(q) || vehicleId.includes(q);
        })
      : list;
    return [...filtered].sort((a, b) => {
      if (b.replayEndTime !== a.replayEndTime) return b.replayEndTime - a.replayEndTime;
      return b.percentFaster - a.percentFaster;
    });
  }, [quickPatrolFlags, search]);

  const detailGroups = useMemo(() => {
    const rows = employeeDetails || [];
    const grouped = new Map<
      string,
      {
        key: string;
        tourName: string;
        tourSessionId: number;
        dateLabel: string;
        latestTs: number;
        rows: EmployeeDoubleHitDetail[];
      }
    >();
    for (const row of rows) {
      const dateLabel = formatDatePST(row.currentScanTime);
      const groupKey = `${row.currentTourSessionId}:${dateLabel}`;
      const existing = grouped.get(groupKey);
      if (!existing) {
        grouped.set(groupKey, {
          key: groupKey,
          tourName: row.currentTourName || `Tour Session ${row.currentTourSessionId}`,
          tourSessionId: row.currentTourSessionId,
          dateLabel,
          latestTs: row.currentScanTime,
          rows: [row],
        });
      } else {
        existing.rows.push(row);
        if (row.currentScanTime > existing.latestTs) existing.latestTs = row.currentScanTime;
      }
    }
    return Array.from(grouped.values())
      .map((group) => ({
        ...group,
        rows: [...group.rows].sort((a, b) => b.currentScanTime - a.currentScanTime),
      }))
      .sort((a, b) => b.latestTs - a.latestTs);
  }, [employeeDetails]);

  const isLoadingList = !isTrackerSelected
    ? false
    : isPatrolReplayTracker
    ? patrolStats === undefined
    : isQuickPatrolTracker
    ? quickPatrolFlags === undefined
    : employeeFlags === undefined;

  const openTracker = (nextTracker: TrackerType) => {
    setTracker(nextTracker);
    setSearch("");
    setSelectedEmployee(null);
  };

  const handleQuickPatrolReplay = async (visitId: string) => {
    if (!onQuickPatrolReplay) return;
    const normalizedVisitId = typeof visitId === "string" ? visitId.trim() : "";
    if (!normalizedVisitId) return;

    setLoadingQuickReplayVisitId(normalizedVisitId);
    try {
      const visit = await getQuickPatrolReplayVisit({ visitId: normalizedVisitId });
      if (visit) onQuickPatrolReplay(visit);
    } catch (error) {
      console.error("Failed to load quick patrol replay visit:", error);
    } finally {
      setLoadingQuickReplayVisitId((current) =>
        current === normalizedVisitId ? null : current
      );
    }
  };

  useEffect(() => {
    if (!onHeaderStateChange) return;
    const title =
      tracker === "patrol_replay"
        ? "Patrol Averages"
        : tracker === "employee_double_hits"
          ? "Double Hits"
          : tracker === "quick_patrols"
            ? "Quick Patrols"
          : "Analytics";
    onHeaderStateChange({ title });
  }, [onHeaderStateChange, tracker]);

  useEffect(() => {
    if (!sidebarBackRequestNonce) return;
    if (sidebarBackRequestNonce === lastHandledSidebarBackRef.current) return;
    lastHandledSidebarBackRef.current = sidebarBackRequestNonce;

    // Step back one level per click:
    // detail -> tracker list, tracker list -> all trackers.
    if (selectedEmployee) {
      setSelectedEmployee(null);
      return;
    }
    if (tracker !== null) {
      setTracker(null);
      setSearch("");
      return;
    }
  }, [sidebarBackRequestNonce, selectedEmployee, tracker]);

  /* ════════════════════════════════════════
     VIEW: Dashboard Landing / Tracker Picker
     ════════════════════════════════════════ */
  if (!isTrackerSelected) {
    return (
      <div className="an-dashboard an-dashboard--main">
        <header className="an-hero">
          <h1 className="an-hero__title">Analytics</h1>
          <p className="an-hero__desc">Select a tracker to review patrol activity, quick runs, and employee flags.</p>
        </header>

        {/* Tracker cards */}
        <section className="an-tracker-grid" aria-label="Analytics tracker selector">
          <button
            type="button"
            className="an-tracker-card"
            onClick={() => openTracker("patrol_replay")}
            aria-label="Open Patrol Averages tracker"
          >
            <div className="an-tracker-card__accent an-tracker-card__accent--replay" />
            <div className="an-tracker-card__icon"><IconReplay /></div>
            <div className="an-tracker-card__body">
              <span className="an-tracker-card__kicker">Tracker</span>
              <h2 className="an-tracker-card__title">Patrol Averages</h2>
              <p className="an-tracker-card__meta">Site durations and replay activity.</p>
            </div>
            <span className="an-tracker-card__cta">
              View report <IconArrowRight />
            </span>
          </button>

          <button
            type="button"
            className="an-tracker-card"
            onClick={() => openTracker("employee_double_hits")}
            aria-label="Open Double Hits tracker"
          >
            <div className="an-tracker-card__accent an-tracker-card__accent--flags" />
            <div className="an-tracker-card__icon"><IconFlag /></div>
            <div className="an-tracker-card__body">
              <span className="an-tracker-card__kicker">Tracker</span>
              <h2 className="an-tracker-card__title">Double Hits</h2>
              <p className="an-tracker-card__meta">Flags by employee and affected tours.</p>
            </div>
            <span className="an-tracker-card__cta">
              View report <IconArrowRight />
            </span>
          </button>

          <button
            type="button"
            className="an-tracker-card"
            onClick={() => openTracker("quick_patrols")}
            aria-label="Open Quick Patrols tracker"
          >
            <div className="an-tracker-card__accent an-tracker-card__accent--quick" />
            <div className="an-tracker-card__icon"><IconBolt /></div>
            <div className="an-tracker-card__body">
              <span className="an-tracker-card__kicker">Tracker</span>
              <h2 className="an-tracker-card__title">Quick Patrols</h2>
              <p className="an-tracker-card__meta">Flags runs completed in half the average site time.</p>
            </div>
            <span className="an-tracker-card__cta">
              View report <IconArrowRight />
            </span>
          </button>
        </section>
      </div>
    );
  }

  /* ════════════════════════════════════════
     VIEW: Employee Detail Drilldown
     ════════════════════════════════════════ */
  if (selectedEmployee && isEmployeeTracker) {
    const detailLoading = employeeDetails === undefined;
    const employeeLabel = getEmployeeLabel(selectedEmployee.employeeName, selectedEmployee.employeeId);

    return (
      <div className="an-dashboard an-dashboard--detail">
        <header className="an-detail-header">
          <div className="an-detail-header__profile">
            <div className="an-avatar">{getInitials(selectedEmployee.employeeName)}</div>
            <div>
              <div className="an-detail-header__badge">Employee Flag Profile</div>
              <h1 className="an-detail-header__name">{employeeLabel}</h1>
              <p className="an-detail-header__desc">
                Drilldown by tour session and date. Every record shows exact scan times that triggered a flag.
              </p>
            </div>
          </div>
        </header>

        {detailLoading ? (
          <div className="an-loading">
            <div className="an-spinner" />
            <span>Loading employee flags...</span>
          </div>
        ) : detailGroups.length === 0 ? (
          <div className="an-empty">
            <IconFlag />
            <span>No flag details found for this employee.</span>
          </div>
        ) : (
          <section className="an-timeline">
            {detailGroups.map((group, groupIdx) => (
              <article key={group.key} className="an-timeline-group" style={{ animationDelay: `${groupIdx * 60}ms` }}>
                <header className="an-timeline-group__head">
                  <div>
                    <div className="an-timeline-group__session">Tour Session {group.tourSessionId}</div>
                    <h3 className="an-timeline-group__tour">{group.tourName}</h3>
                  </div>
                  <div className="an-timeline-group__date">{group.dateLabel}</div>
                </header>

                <div className="an-timeline-group__flags">
                  {group.rows.map((row) => {
                    const minutesBetween = normalizeMinutesBetween(
                      row.minutesBetween,
                      row.previousScanTime,
                      row.currentScanTime
                    );
                    const previousCheckpointLabel =
                      row.previousCheckpointName || `Checkpoint ${row.previousCheckpointId}`;
                    const currentCheckpointLabel = row.currentCheckpointName || `Checkpoint ${row.currentCheckpointId}`;

                    return (
                      <div key={row.flagKey} className="an-flag-card">
                        <div className="an-flag-card__indicator">
                          <IconAlertTriangle />
                        </div>
                        <div className="an-flag-card__body">
                          <div className="an-flag-card__site">{row.siteName || "Site"}</div>
                          <div className="an-flag-card__detail an-flag-card__detail--stack">
                            <div className="an-flag-card__line">
                              <span className="an-flag-card__checkpoint">{previousCheckpointLabel}</span>
                              <span className="an-flag-card__divider">-</span>
                              <strong>{formatClockPST(row.previousScanTime)}</strong>
                            </div>
                            <div className="an-flag-card__line">
                              <span className="an-flag-card__checkpoint">{currentCheckpointLabel}</span>
                              <span className="an-flag-card__divider">-</span>
                              <strong>{formatClockPST(row.currentScanTime)}</strong>
                            </div>
                          </div>
                          <div className="an-flag-card__gap">
                            <IconClock /> {minutesBetween} min apart
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            ))}
          </section>
        )}
      </div>
    );
  }

  /* ════════════════════════════════════════
     VIEW: Loading State
     ════════════════════════════════════════ */
  if (isLoadingList) {
    return (
      <div className="an-dashboard an-dashboard--main">
        <div className="an-loading">
          <div className="an-spinner" />
          <span>Loading analytics...</span>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════
     VIEW: Tracker List (Patrol or Employee)
     ════════════════════════════════════════ */
  return (
    <div className="an-dashboard an-dashboard--main">
      {/* Search + Sort toolbar */}
      <div className="an-toolbar">
        <div className="an-toolbar__search">
          <span className="an-toolbar__search-icon"><IconSearch /></span>
          <input
            className="an-toolbar__input"
            type="text"
            placeholder={
              isPatrolReplayTracker
                ? "Search sites..."
                : isQuickPatrolTracker
                  ? "Search guards, vehicles, or sites..."
                  : "Search employees..."
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button type="button" className="an-toolbar__clear" onClick={() => setSearch("")} aria-label="Clear search">
              &times;
            </button>
          )}
        </div>
        {isEmployeeTracker && (
          <div className="an-toolbar__sort">
            <span className="an-toolbar__sort-icon"><IconSort /></span>
            <select
              className="an-toolbar__select"
              value={employeeSortBy}
              onChange={(e) => setEmployeeSortBy(e.target.value as EmployeeSortBy)}
            >
              <option value="flags_desc">Flags (High → Low)</option>
              <option value="flags_asc">Flags (Low → High)</option>
              <option value="tours_desc">Tours (High → Low)</option>
              <option value="tours_asc">Tours (Low → High)</option>
              <option value="recent_desc">Most Recent Flag</option>
              <option value="name_asc">Employee Name (A → Z)</option>
              <option value="name_desc">Employee Name (Z → A)</option>
            </select>
          </div>
        )}
      </div>

      {/* Backfill banner */}
      {isEmployeeTracker && processingStatus && !processingStatus.backfillCompleted && (
        <div className="an-banner">
          <IconActivity />
          <span>
            Backfill in progress: processed {processingStatus.totalScansProcessed} scans and generated
            {` ${processingStatus.totalFlagsCreated} `}flags so far.
          </span>
        </div>
      )}

      {/* Card grid */}
      {isPatrolReplayTracker && (
        patrolRows.length === 0 ? (
          <div className="an-empty">
            <IconReplay />
            <span>No patrol replay data found yet.</span>
          </div>
        ) : (
          <section className="an-card-grid">
            {patrolRows.map((item, idx) => {
              const avgMs = item.replayCount > 0 ? item.totalDurationMs / item.replayCount : 0;
              const siteLabel = item.siteName || "Unknown Site";
              return (
                <article key={item.siteId} className="an-card" style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}>
                  <header className="an-card__head">
                    <div className="an-card__title">{siteLabel}</div>
                  </header>

                  <div className="an-card__stats">
                    <div className="an-card__stat">
                      <div className="an-card__stat-label">Avg Duration</div>
                      <div className="an-card__stat-value">{formatDuration(avgMs)}</div>
                    </div>
                    <div className="an-card__stat">
                      <div className="an-card__stat-label">Patrols</div>
                      <div className="an-card__stat-value">{item.replayCount}</div>
                    </div>
                    <div className="an-card__stat">
                      <div className="an-card__stat-label">Last Patrol</div>
                      <div className="an-card__stat-value">{formatDateTimeLocal(item.lastReplayEndTime)}</div>
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        )
      )}

      {isQuickPatrolTracker && (
        quickPatrolRows.length === 0 ? (
          <div className="an-empty">
            <IconBolt />
            <span>No quick patrol flags found in the selected lookback window.</span>
          </div>
        ) : (
          <section className="an-card-grid">
            {quickPatrolRows.map((item, idx) => {
              const isReplayClickable = !!onQuickPatrolReplay;
              const isReplayLoading = loadingQuickReplayVisitId === item.visitId;

              return (
                <article
                  key={item.visitId}
                  className={`an-card${isReplayClickable ? " an-card--btn" : ""}`}
                  style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                  onClick={
                    isReplayClickable
                      ? () => {
                          if (isReplayLoading) return;
                          void handleQuickPatrolReplay(item.visitId);
                        }
                      : undefined
                  }
                  onKeyDown={
                    isReplayClickable
                      ? (event) => {
                          if (isReplayLoading) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            void handleQuickPatrolReplay(item.visitId);
                          }
                        }
                      : undefined
                  }
                  role={isReplayClickable ? "button" : undefined}
                  tabIndex={isReplayClickable ? 0 : undefined}
                  aria-busy={isReplayLoading || undefined}
                >
                  <header className="an-card__head">
                    <div className="an-card__head-main">
                      <div className="an-card__title">{item.siteName || "Unknown Site"}</div>
                      <div className="an-card__meta-line">
                        {item.guardName || "Unassigned"} - {item.vehicleName || item.vehicleId}
                      </div>
                    </div>
                    <span className="an-card__tag an-card__tag--quick">{item.percentFaster.toFixed(1)}% faster</span>
                  </header>

                  <div className="an-card__stats">
                    <div className="an-card__stat">
                      <div className="an-card__stat-label">Patrol Duration</div>
                      <div className="an-card__stat-value">{formatDuration(item.patrolDurationMs)}</div>
                    </div>
                    <div className="an-card__stat">
                      <div className="an-card__stat-label">Site Avg</div>
                      <div className="an-card__stat-value">{formatDuration(item.averageDurationMs)}</div>
                    </div>
                    <div className="an-card__stat">
                      <div className="an-card__stat-label">50% Threshold</div>
                      <div className="an-card__stat-value">{formatDuration(item.quickThresholdMs)}</div>
                    </div>
                  </div>

                  <div className="an-card__meta-row">
                    <span>Flagged {formatDateTimePSTCompact(item.replayEndTime)}</span>
                    <span>{item.sampleSize} patrol average</span>
                  </div>
                </article>
              );
            })}
          </section>
        )
      )}

      {isEmployeeTracker && (
        employeeRows.length === 0 ? (
          <div className="an-empty">
            <IconFlag />
            <span>No employee double-hit flags found yet.</span>
          </div>
        ) : (
          <section className="an-card-grid an-card-grid--emp">
            {employeeRows.map((item, idx) => {
              const employeeLabel = getEmployeeLabel(item.employeeName, item.employeeId);
              return (
                <button
                  key={item.employeeId}
                  type="button"
                  className="an-card an-card--btn"
                  onClick={() => setSelectedEmployee(item)}
                  style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                >
                  <header className="an-card__head">
                    <div className="an-card__head-left">
                      <div className="an-avatar an-avatar--sm">{getInitials(item.employeeName)}</div>
                      <div className="an-card__title">{employeeLabel}</div>
                    </div>
                    <span className="an-card__tag an-card__tag--emp">Employee</span>
                  </header>

                  <div className="an-card__stats">
                    <div className="an-card__stat">
                      <div className="an-card__stat-label">Flags</div>
                      <div className="an-card__stat-value">{item.flagCount}</div>
                    </div>
                    <div className="an-card__stat">
                      <div className="an-card__stat-label">Tours</div>
                      <div className="an-card__stat-value">{item.flaggedTourCount}</div>
                    </div>
                    <div className="an-card__stat">
                      <div className="an-card__stat-label">Last Flag</div>
                      <div className="an-card__stat-value">{formatDateTimePSTCompact(item.lastFlagTime)}</div>
                    </div>
                  </div>

                  <span className="an-card__cta">
                    View flagged tours <IconArrowRight />
                  </span>
                </button>
              );
            })}
          </section>
        )
      )}
    </div>
  );
}
